import { cn } from "@/lib/utils";
import { formatInTz } from "./dateUtils";
import type { CalendarEventListItem } from "./types";

interface EventRowProps {
  event: CalendarEventListItem;
  tz: string;
  selected: boolean;
  onSelect: (id: string) => void;
}

function importanceDot(imp: string): string {
  return imp === "high" || imp === "medium" || imp === "low" ? "●" : "·";
}

function importanceColor(imp: string): string {
  if (imp === "high") return "text-red-500";
  if (imp === "medium") return "text-amber-500";
  if (imp === "low") return "text-emerald-500";
  return "text-muted-foreground";
}

function surprise(
  actual: string | null,
  forecast: string | null,
): { glyph: string; tone: string } | null {
  if (!actual) return null;
  if (!forecast) return { glyph: "◇", tone: "text-muted-foreground" };
  const a = parseFloat(actual.replace(/[^\d.\-]/g, ""));
  const f = parseFloat(forecast.replace(/[^\d.\-]/g, ""));
  if (isNaN(a) || isNaN(f)) return { glyph: "◇", tone: "text-muted-foreground" };
  if (a > f) return { glyph: "▲", tone: "text-emerald-400" };
  if (a < f) return { glyph: "▼", tone: "text-red-400" };
  return { glyph: "▬", tone: "text-muted-foreground" };
}

export function EventRow({ event, tz, selected, onSelect }: EventRowProps) {
  const isPast = event.status === "occurred" || event.status === "revised";
  const time = event.scheduled_at_utc
    ? formatInTz(event.scheduled_at_utc, tz, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
    : "All day";
  const surpr = isPast ? surprise(event.actual, event.forecast) : null;
  return (
    <button
      type="button"
      onClick={() => onSelect(event.id)}
      className={cn(
        "flex w-full items-center gap-3 rounded-md border px-3 py-1.5 text-start text-xs",
        selected
          ? "border-emerald-500/60 bg-card"
          : "border-border bg-card hover:bg-muted/40",
        isPast && "opacity-70",
      )}
    >
      <span className="w-12 shrink-0 font-mono tabular-nums text-muted-foreground">
        {time}
      </span>
      <span className={cn("w-3 shrink-0", importanceColor(event.importance))}>
        {isPast
          ? surpr?.glyph === "▲"
            ? "✓"
            : surpr?.glyph === "▼"
              ? "✗"
              : "◇"
          : importanceDot(event.importance)}
      </span>
      <span className="flex-1 truncate">{event.title}</span>
      {event.materials.slice(0, 3).map((m) => (
        <span
          key={m}
          className="shrink-0 rounded-full bg-sky-500/10 px-1.5 text-[10px] text-sky-400"
        >
          {m}
        </span>
      ))}
      {event.materials.length > 3 && (
        <span className="shrink-0 text-[10px] text-muted-foreground">
          +{event.materials.length - 3}
        </span>
      )}
      {surpr ? (
        <span className={cn("shrink-0 font-mono tabular-nums", surpr.tone)}>
          {surpr.glyph} {event.actual} vs {event.forecast}
        </span>
      ) : event.forecast ? (
        <span className="shrink-0 text-muted-foreground">
          Fcst {event.forecast}
          {event.previous && ` · Prev ${event.previous}`}
        </span>
      ) : null}
      {event.source_trust === "low" && (
        <span className="shrink-0 rounded-full bg-amber-500/10 px-1.5 text-[10px] text-amber-400">
          low-trust
        </span>
      )}
    </button>
  );
}
